import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import '../assets/styles/ParentDashboard.css';
import '../assets/styles/ChoreCards.css';
import '../assets/styles/CompletedChores.css';
import ChoreCard from './ChoreCard';
import KidAvatarSettings from './KidAvatarSettings';
import StreakTracker from './StreakTracker';
import AchievementShowcase from './AchievementShowcase';
import SmartStore from './SmartStore';
import QuestProgress from './QuestProgress';
import FriendsPanel from './FriendsPanel';
import GoalVisualization from './GoalVisualization';
import FinancialLiteracy from './FinancialLiteracy';
import { saveUser } from '../utils/auth';

const KidDashboard = ({ user, onLogout }) => {
    const [balance, setBalance] = useState(0);
    const [chores, setChores] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [activeGoal, setActiveGoal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAvatarSettings, setShowAvatarSettings] = useState(false);
    const [currentUser, setCurrentUser] = useState(user);

    // Surprise Bonus State
    const [unreadBonuses, setUnreadBonuses] = useState([]);
    const [showBonusCelebration, setShowBonusCelebration] = useState(false);
    const [currentBonus, setCurrentBonus] = useState(null);
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(true);

    const toggleTheme = () => {
        const currentTheme = document.documentElement.getAttribute('data-theme');
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('theme', newTheme);
    };

    const fetchKidData = useCallback(async (silent = false) => {
        const userId = user?.user_id || user?.id;
        const tenantId = user?.tenant_id;

        if (!userId || !tenantId) {
            return;
        }

        if (!silent) {
            setLoading(true);
        }
        try {
            const balanceRes = await axios.get(`/api/v1/balance?kid_id=${userId}`);
            setBalance(balanceRes.data.balance || 0);

            const choresRes = await axios.get(`/api/v1/chores?household_id=${tenantId}&kid_id=${userId}`);
            setChores(choresRes.data || []);

            // Fetch submissions to filter out already submitted chores
            const submissionsRes = await axios.get(`/api/v1/submissions?household_id=${tenantId}&kid_id=${userId}`);
            setSubmissions(submissionsRes.data || []);

            // Fetch goals for this kid
            const goalRes = await axios.get(`/api/v1/goals?kid_id=${userId}`);
            const goals = goalRes.data || [];

            // Set the first goal as active (or the one marked as active_goal_id if it exists)
            if (goals.length > 0) {
                const active = user.active_goal_id
                    ? goals.find(g => g.id === user.active_goal_id) || goals[0]
                    : goals[0];
                setActiveGoal(active);
            }
        } catch (err) {
            console.error('Failed to fetch kid data:', err);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [user]);

    useEffect(() => {
        fetchKidData();

        // Poll for updates every 10 seconds (silent refresh)
        const pollInterval = setInterval(() => {
            fetchKidData(true); // Silent refresh
        }, 10000); // 10 seconds

        // Cleanup interval on unmount
        return () => clearInterval(pollInterval);
    }, [fetchKidData]);

    // Fetch unread surprise bonuses
    useEffect(() => {
        const fetchUnreadBonuses = async () => {
            try {
                const response = await axios.get('/api/v1/bonus/unread');
                const bonuses = response.data || [];
                setUnreadBonuses(bonuses);

                // Show celebration for first unread bonus
                if (bonuses.length > 0 && !showBonusCelebration) {
                    setCurrentBonus(bonuses[0]);
                    setShowBonusCelebration(true);
                }
            } catch (err) {
                console.error('Failed to fetch unread bonuses:', err);
            }
        };

        fetchUnreadBonuses();

        // Check for new bonuses every 30 seconds
        const bonusInterval = setInterval(fetchUnreadBonuses, 30000);
        return () => clearInterval(bonusInterval);
    }, [showBonusCelebration]);

    const dismissBonusCelebration = () => {
        setShowBonusCelebration(false);
        // Show next bonus if there are more
        const remainingBonuses = unreadBonuses.filter(b => b.id !== currentBonus?.id);
        if (remainingBonuses.length > 0) {
            setTimeout(() => {
                setCurrentBonus(remainingBonuses[0]);
                setShowBonusCelebration(true);
            }, 500);
        }
        setCurrentBonus(null);
    };

    const formatCurrency = (amount) => {
        return `$${(amount / 100).toFixed(2)}`;
    };

    const pendingChores = chores.filter(c => c.status === 'active').length;
    const goalProgress = activeGoal ? Math.min(100, (balance / activeGoal.target_amount) * 100) : 0;

    if (showAvatarSettings) {
        return <KidAvatarSettings
            user={currentUser}
            onBack={() => setShowAvatarSettings(false)}
            onAvatarUpdate={(updatedUser) => {
                setCurrentUser(updatedUser);
                // Save to localStorage so avatar persists across sessions
                saveUser(updatedUser);
            }}
        />;
    }

    return (
        <div className="dashboard-container kid-theme">
            <header className="dashboard-header">
                <div className="header-left">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div
                            onClick={() => setShowAvatarSettings(true)}
                            style={{
                                fontSize: '48px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                                width: '64px',
                                height: '64px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '3px solid #ec4899',
                                boxShadow: '0 4px 12px rgba(236, 72, 153, 0.2)'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            title="Click to change avatar"
                            role="button"
                            tabIndex={0}
                            aria-label="Change your avatar"
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowAvatarSettings(true); }}
                        >
                            {currentUser?.avatar || '😊'}
                        </div>
                        <div>
                            <h1>Kid Dashboard</h1>
                            <p className="subtitle pink-text">Welcome back, {currentUser?.display_name || currentUser?.username}! 🎉</p>
                        </div>
                    </div>
                </div>
                <div className="header-right">
                    <button
                        className="btn-icon"
                        onClick={toggleTheme}
                        style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', marginRight: '15px' }}
                        title="Toggle Dark Mode"
                        aria-label="Toggle dark mode"
                    >
                        🌙
                    </button>
                    <div className="notification-bell" style={{ position: 'relative', marginRight: '20px', cursor: 'pointer', fontSize: '1.2rem' }} onClick={() => setHasUnreadNotifications(false)} role="button" tabIndex={0} aria-label={hasUnreadNotifications ? 'Notifications (unread)' : 'Notifications'} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setHasUnreadNotifications(false); }}>
                        🔔
                        {hasUnreadNotifications && (
                            <span className="unread-dot" style={{
                                position: 'absolute',
                                top: '0px',
                                right: '-2px',
                                width: '10px',
                                height: '10px',
                                backgroundColor: '#ef4444',
                                borderRadius: '50%',
                                border: '2px solid white'
                            }}></span>
                        )}
                    </div>
                    <div className="user-badge kid-badge">
                        <div className="user-avatar kid-avatar">{user?.username?.[0]?.toUpperCase() || 'K'}</div>
                        <span>Welcome, {user?.username}!</span>
                        <button
                            onClick={onLogout}
                            style={{
                                marginLeft: '12px',
                                padding: '8px 16px',
                                background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(236, 72, 153, 0.2)'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 8px rgba(236, 72, 153, 0.3)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 4px rgba(236, 72, 153, 0.2)';
                            }}
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* 1. Stats Bar - Quick Overview of Wallet & Tasks */}
            <div className="stats-bar kid-stats" style={{ marginBottom: '24px' }}>
                <div className="stat-item highlight-card">
                    <div className="stat-label">Your Wallet 💰</div>
                    <div className="stat-value big-text">{formatCurrency(balance)}</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">Chores to do ✅</div>
                    <div className="stat-value">{pendingChores}</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">Next Goal 🎯</div>
                    <div className="stat-value">{activeGoal ? activeGoal.title : 'None Set'}</div>
                </div>
            </div>

            {/* 2. Active Chores - THE MOST IMPORTANT PART */}
            {loading ? (
                <div className="loading" style={{ marginBottom: '32px' }}>Loading your chores...</div>
            ) : (
                <div style={{ marginBottom: '32px' }}>
                    {chores.filter(chore => {
                        const hasSubmission = submissions.some(
                            sub => sub.challenge_id === chore.id && (sub.status === 'pending' || sub.status === 'approved')
                        );
                        return !hasSubmission;
                    }).length > 0 ? (
                        <section className="chores-section">
                            <h2>🧹 Earn DadBucks - Complete These Chores!</h2>
                            <div className="chores-grid">
                                {chores.filter(chore => {
                                    const hasSubmission = submissions.some(
                                        sub => sub.challenge_id === chore.id && (sub.status === 'pending' || sub.status === 'approved')
                                    );
                                    return !hasSubmission;
                                }).map(chore => {
                                    const userId = user?.user_id || user?.id;
                                    return (
                                        <ChoreCard
                                            key={chore.id}
                                            chore={chore}
                                            kidUserId={userId}
                                            onSubmit={fetchKidData}
                                            formatCurrency={formatCurrency}
                                        />
                                    );
                                })}
                            </div>
                        </section>
                    ) : (
                        <section className="onboarding-card welcome-kid">
                            <span className="large-emoji">🦁</span>
                            <h2>You're In, {user?.username}!</h2>
                            <p>Welcome to DadBucks. Your parent will assign you chores soon. When you finish them, you earn real rewards!</p>
                        </section>
                    )}
                </div>
            )}

            {/* Weekly Quests */}
            <QuestProgress kidUserId={user?.user_id || user?.id} />

            <FriendsPanel kidUserId={user?.user_id || user?.id} />

            {/* Goal Visualization */}
            <GoalVisualization goal={activeGoal} currentBalance={balance} />

            {/* 4. Milestone Tracker (Streaks) & Achievements - Below main actions */}
            <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '20px', marginBottom: '32px' }}>
                <h3 style={{ marginBottom: '20px', color: '#64748b' }}>🏆 Milestones & Achievements</h3>
                <div style={{ marginBottom: '24px' }}>
                    <StreakTracker kidUserId={user?.user_id || user?.id} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <AchievementShowcase kidUserId={user?.user_id || user?.id} />
                </div>
            </div>

            {/* Financial Literacy */}
            <section style={{ marginBottom: '32px' }}>
                <FinancialLiteracy user={user} />
            </section>

            <section className="chores-section">
                <SmartStore kidUserId={user?.user_id || user?.id} />
            </section>


            {submissions.filter(s => s.status === 'approved').length > 0 && (
                <section className="chores-section">
                    <h2>✅ Completed Chores</h2>
                    <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                        Great work! Here are the chores you've completed and gotten paid for.
                    </p>
                    <div className="chores-grid">
                        {submissions
                            .filter(s => s.status === 'approved')
                            .sort((a, b) => new Date(b.approved_at || b.submitted_at) - new Date(a.approved_at || a.submitted_at))
                            .map(submission => {
                                const chore = chores.find(c => c.id === submission.challenge_id);
                                if (!chore) return null;
                                return (
                                    <div key={submission.id} className="chore-card completed">
                                        <div className="chore-header">
                                            <h3>{chore.title}</h3>
                                            <span className="chore-reward completed-reward">
                                                ✓ {formatCurrency(chore.reward_amount)}
                                            </span>
                                        </div>
                                        {chore.description && (
                                            <p className="chore-description">{chore.description}</p>
                                        )}
                                        {submission.notes && (
                                            <p className="submission-notes-kid">
                                                <strong>Your note:</strong> "{submission.notes}"
                                            </p>
                                        )}
                                        <div className="completed-info">
                                            <p className="completed-date">
                                                <strong>Completed:</strong> {new Date(submission.approved_at || submission.submitted_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </section>
            )}

            {/* Surprise Bonus Celebration Modal */}
            {showBonusCelebration && currentBonus && (
                <div
                    className="modal-overlay"
                    onClick={dismissBonusCelebration}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        animation: 'fadeIn 0.3s ease'
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Surprise bonus notification"
                        style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            borderRadius: '24px',
                            padding: '48px',
                            textAlign: 'center',
                            color: 'white',
                            maxWidth: '400px',
                            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
                            animation: 'bounceIn 0.5s ease'
                        }}
                    >
                        <div style={{ fontSize: '80px', marginBottom: '16px' }}>
                            🎁
                        </div>
                        <h2 style={{ fontSize: '2rem', marginBottom: '8px' }}>
                            Surprise Bonus!
                        </h2>
                        <div style={{
                            fontSize: '3rem',
                            fontWeight: 'bold',
                            marginBottom: '16px',
                            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                            +${(currentBonus.amount / 100).toFixed(2)}
                        </div>
                        <p style={{ fontSize: '1.2rem', opacity: 0.95, marginBottom: '8px' }}>
                            {currentBonus.emoji || '🎉'} {currentBonus.reason}
                        </p>
                        {currentBonus.given_by_name && (
                            <p style={{ fontSize: '1rem', opacity: 0.8, marginBottom: '24px' }}>
                                From: {currentBonus.given_by_name}
                            </p>
                        )}
                        <button
                            onClick={dismissBonusCelebration}
                            aria-label="Dismiss bonus notification"
                            style={{
                                background: 'white',
                                color: '#059669',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '14px 32px',
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                            }}
                        >
                            🎉 Awesome!
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KidDashboard;
